import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import orderContext from '../../context/OrderContext';
import OrderPopup from './OrderPopup';

const EXCLUDED_BOM_PARTS_STORAGE_KEY = 'ecommerce-orders-excluded-bom-parts-v1';

const OrderDetailsPage = () => {
    const { orderNum } = useParams();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { fetchOrderValidationData } = orderContext();

    // Notifications deep-link here from different pages (Orders, Order Validation).
    // The `from` param tells us where to return to on close instead of always going to /orders.
    const sourcePage = searchParams.get('from');
    const backTarget = sourcePage === 'order-validation' ? '/order-validation' : '/orders';
    const backLabel = sourcePage === 'order-validation' ? 'Back to Order Validation' : 'Back to Orders';
    
    const [orderData, setOrderData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [orderAccessLinks, setOrderAccessLinks] = useState({});
    const [excludedAvailabilityPartsByOrder, setExcludedAvailabilityPartsByOrder] = useState(() => {
        try {
            const raw = localStorage.getItem(EXCLUDED_BOM_PARTS_STORAGE_KEY);
            if (!raw) return {};
            const parsed = JSON.parse(raw);
            return parsed && typeof parsed === 'object' ? parsed : {};
        } catch {
            return {};
        }
    });

    useEffect(() => {
        try {
            localStorage.setItem(
                EXCLUDED_BOM_PARTS_STORAGE_KEY,
                JSON.stringify(excludedAvailabilityPartsByOrder || {})
            );
        } catch {
            /* ignore */
        }
    }, [excludedAvailabilityPartsByOrder]);

    const handleToggleExcludedAvailabilityPart = useCallback((orderNum, partNumber, shouldExclude) => {
        if (!orderNum || !partNumber) return;
        setExcludedAvailabilityPartsByOrder((prev) => {
            const orderKey = String(orderNum);
            const prevOrderMap = prev[orderKey] || {};
            const nextOrderMap = { ...prevOrderMap };

            if (shouldExclude) {
                nextOrderMap[partNumber] = true;
            } else {
                delete nextOrderMap[partNumber];
            }

            if (Object.keys(nextOrderMap).length === 0) {
                const { [orderKey]: removedOrder, ...rest } = prev;
                return rest;
            }

            return {
                ...prev,
                [orderKey]: nextOrderMap,
            };
        });
    }, []);

    useEffect(() => {
        const fetchOrderDetails = async () => {
            if (!orderNum) {
                setError('No order number provided');
                setLoading(false);
                return;
            }

            try {
                setLoading(true);
                console.log('Fetching order details for order number:', orderNum);
                
                const orders = await fetchOrderValidationData(0, 10, "", "lineitem", "ascending", null, "", "", "", "", "", "", "", "", "", true, true, "", orderNum);
                
                if (orders === null || !orders.order || orders.order.length === 0) {
                    setError(`Order ${orderNum} not found`);
                    setLoading(false);
                    return;
                }

                setOrderData(orders.order);

                // Set up order access links
                if (Array.isArray(orders.order) && orders.order.length > 0) {
                    const newLinks = {};
                    const timestamp = Date.now();
                    const dateLabel = new Date().toLocaleDateString();

                    orders.order.forEach((orderLine) => {
                        const url = orderLine.folderUrl?.trim();
                        if (url) {
                            newLinks[orderLine.id] = [{
                                id: `${timestamp}-${orderLine.id}`,
                                url,
                                dateAdded: dateLabel
                            }];
                        }
                    });

                    if (Object.keys(newLinks).length > 0) {
                        setOrderAccessLinks(newLinks);
                    }
                }

                setLoading(false);
            } catch (err) {
                console.error('Error fetching order details:', err);
                setError(err.message || 'Failed to load order details');
                setLoading(false);
            }
        };

        fetchOrderDetails();
    }, [orderNum]);

    const handleClose = () => {
        navigate(backTarget);
    };

    if (loading) {
        return (
            <div className="container-fluid vh-100 d-flex justify-content-center align-items-center">
                <div className="text-center">
                    <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <div className="mt-3">
                        <h5>Loading Order Details...</h5>
                        <p className="text-muted">Order #{orderNum}</p>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="container-fluid vh-100 d-flex justify-content-center align-items-center">
                <div className="text-center">
                    <div className="alert alert-danger" role="alert">
                        <i className="fa fa-exclamation-triangle" style={{ fontSize: '48px', marginBottom: '20px' }}></i>
                        <h4 className="alert-heading">Error Loading Order</h4>
                        <p>{error}</p>
                        <hr />
                        <button className="btn btn-primary" onClick={handleClose}>
                            <i className="fa fa-arrow-left me-2"></i>
                            {backLabel}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if (!orderData || orderData.length === 0) {
        return (
            <div className="container-fluid vh-100 d-flex justify-content-center align-items-center">
                <div className="text-center">
                    <div className="alert alert-warning" role="alert">
                        <h4 className="alert-heading">Order Not Found</h4>
                        <p>Order #{orderNum} could not be found in the system.</p>
                        <hr />
                        <button className="btn btn-primary" onClick={handleClose}>
                            <i className="fa fa-arrow-left me-2"></i>
                            {backLabel}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="container-fluid" style={{ padding: '20px', minHeight: '100vh', backgroundColor: '#f8f9fa' }}>
            {/* Header with back button and order info */}
            <div className="row mb-3">
                <div className="col-12">
                    <div className="d-flex justify-content-between align-items-center">
                        <div className="d-flex align-items-center gap-3">
                            <button 
                                className="btn btn-outline-primary" 
                                onClick={handleClose}
                                title={backLabel}
                            >
                                <i className="fa fa-arrow-left me-2"></i>
                                {backLabel}
                            </button>
                            <h3 className="mb-0">
                                Order Details - #{orderNum}
                            </h3>
                        </div>
                        <div className="d-flex align-items-center gap-2">
                            <a 
                                href={`https://gigaflightinc.cetecerp.com/order/MN${orderNum}/view`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn btn-outline-secondary"
                            >
                                <i className="fa fa-external-link me-2"></i>
                                View in Cetec
                            </a>
                        </div>
                    </div>
                </div>
            </div>

            {/* Order Details Content */}
            <div className="row">
                <div className="col-12">
                    <div className="box-body border bg-white rounded-2 p-4">
                        <OrderPopup
                            show={true}
                            onClose={handleClose}
                            orderData={orderData}
                            orderAccessLinks={orderAccessLinks}
                            excludedAvailabilityPartsByOrder={excludedAvailabilityPartsByOrder}
                            onToggleExcludedAvailabilityPart={handleToggleExcludedAvailabilityPart}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default OrderDetailsPage;
